import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { supabase } from './supabase';
import { db, auth, getCollectionName } from './firebase';
import { collection, getDocs, doc, updateDoc, query, where } from './firestore-compat';
import { DISPOSITIVOS_IW, montarControleTrocas, fmtData } from './iw/controleTrocas';

const P = {
  bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#475569',
  primary: '#4f46e5', primaryDark: '#3730a3', primarySoft: '#eef2ff',
};
const SIGLA = Object.fromEntries(DISPOSITIVOS_IW.map(d => [d.id, d.sigla]));
const NOME_FAMILIA = { gtt: 'Gastrostomia', tqt: 'Traqueostomia', cvd: 'Sonda vesical', picc: 'PICC', sne: 'Sonda nasoenteral' };
const UNIDADES = ['Todas', 'VitalCare', 'HomeBaby'];
const POR_PAGINA = [25, 50, 100];

async function buscarPacientesIW() {
  const { data: { session } } = await supabase.auth.getSession();
  const resp = await fetch('/api/iw-pacientes', { headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(json.error || `Erro ${resp.status}`);
  return json;
}

async function lerColecao(nome) {
  const snap = await getDocs(collection(db, getCollectionName(nome)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

function Paginacao({ total, pagina, setPagina, porPagina, setPorPagina }) {
  const paginas = Math.max(1, Math.ceil(total / porPagina));
  const btn = { border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '5px 10px', fontSize: '0.78rem', cursor: 'pointer' };
  return (
    <div style={{ padding: '12px 18px', borderTop: `1px solid ${P.border}`, fontSize: '0.8rem', color: P.muted, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
      <span>{total === 0 ? 'Nenhum registro' : `Mostrando ${(pagina - 1) * porPagina + 1}–${Math.min(total, pagina * porPagina)} de ${total}`}</span>
      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <label>Por página{' '}
          <select value={porPagina} onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }} style={{ ...btn, padding: '4px 6px' }}>
            {POR_PAGINA.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button style={btn} disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Anterior</button>
        <span>{pagina} / {paginas}</span>
        <button style={btn} disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>Próxima</button>
      </span>
    </div>
  );
}

const thStyle = { padding: '11px 14px', fontWeight: 700 };
const tdStyle = { padding: '11px 14px' };
const chip = (fundo, cor) => ({ fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap' });

// embutido: dentro da tela Dispositivos (aba "Pacientes com dispositivo") — sem cabeçalho e
// sem a aba de trocas, que virou o "Painel de trocas".
export default function ControleDispositivos({ embutido = false }) {
  const [iw, setIw] = useState(null);
  const [trocas, setTrocas] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  const [aba, setAba] = useState('uso');
  const [unidade, setUnidade] = useState('Todas');
  const [disp, setDisp] = useState(null);
  const [situacao, setSituacao] = useState(null);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);

  // Permissão de editar (mesmo padrão das outras telas: super admin, admin ou permissão "editar" do módulo).
  const [perfil, setPerfil] = useState({ roles: [], perms: {}, nome: '' });
  const currentUser = auth.currentUser;
  useEffect(() => {
    if (!currentUser?.email) return;
    getDocs(query(collection(db, getCollectionName('users')), where('email', '==', currentUser.email.toLowerCase())))
      .then(snap => {
        const u = snap.docs[0]?.data() || {};
        setPerfil({ roles: Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : []), perms: u.permissions || {}, nome: u.name || currentUser.email });
      })
      .catch(() => {});
  }, [currentUser]);
  const podeEditar = currentUser?.email === 'alexandrebareaoliveira@gmail.com' || perfil.roles.includes('admin') || perfil.roles.includes('administrador') || perfil.perms?.controle_dispositivos?.edit === true;
  const [salvando, setSalvando] = useState('');

  const definirLocal = async (linha, local) => {
    if (!linha.id) return;
    setSalvando(linha.id);
    try {
      await updateDoc(doc(db, getCollectionName('trocas_dispositivos'), linha.id), {
        local_proxima_troca: local,
        local_definido_por: perfil.nome || currentUser?.email || '',
        local_definido_em: new Date().toISOString(),
      });
      setTrocas(ts => ts.map(t => (t.id === linha.id ? { ...t, local_proxima_troca: local, local_definido_por: perfil.nome || currentUser?.email || '' } : t)));
    } catch (e) {
      alert('Não foi possível salvar: ' + e.message);
    }
    setSalvando('');
  };

  const carregar = async () => {
    setCarregando(true); setErro('');
    try {
      const [dadosIW, t, s] = await Promise.all([buscarPacientesIW(), lerColecao('trocas_dispositivos'), lerColecao('dispositivos_solicitacoes')]);
      setIw(dadosIW); setTrocas(t); setSolicitacoes(s);
    } catch (e) {
      setErro(e.message);
    }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);
  useEffect(() => { setPagina(1); }, [aba, unidade, disp, situacao, busca]);

  const pacientes = useMemo(() => iw?.pacientes || [], [iw]);
  const daUnidade = useMemo(() => pacientes.filter(p => unidade === 'Todas' || p.unidade === unidade), [pacientes, unidade]);
  const contagemUnidade = useMemo(() => {
    const c = { Todas: pacientes.length, VitalCare: 0, HomeBaby: 0 };
    pacientes.forEach(p => { if (c[p.unidade] !== undefined) c[p.unidade]++; });
    return c;
  }, [pacientes]);

  const cards = useMemo(() => DISPOSITIVOS_IW.map(d => {
    const sim = daUnidade.filter(p => p.dispositivos[d.id]?.valor === 'sim');
    const semInfo = daUnidade.filter(p => p.dispositivos[d.id]?.valor === 'semInfo').length;
    let detalhe = `${semInfo} sem informação`;
    if (d.id === 'o2') {
      const sos = sim.filter(p => /sos/i.test(p.dispositivos.o2.texto)).length;
      const cont = sim.filter(p => /cont/i.test(p.dispositivos.o2.texto)).length;
      detalhe = `${sos} SOS · ${cont} contínuo`;
    }
    return { ...d, sim: sim.length, detalhe };
  }), [daUnidade]);

  const pct = n => (daUnidade.length ? Math.round((n / daUnidade.length) * 100) : 0);
  const q = busca.trim().toLowerCase();
  const casaBusca = p => !q || (p.nome || '').toLowerCase().includes(q) || String(p.numIW || '').includes(q);

  // ── Aba "Dispositivos em uso" ──
  const linhasUso = useMemo(() => daUnidade.filter(p =>
    casaBusca(p) &&
    (!disp || (disp === 'semInfo'
      ? ['gtt', 'tqt', 'cvd'].some(k => p.dispositivos[k]?.valor === 'semInfo')
      : p.dispositivos[disp]?.valor === 'sim'))
  ).sort((a, b) => a.nome.localeCompare(b.nome)), [daUnidade, disp, q]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Aba "Controle de trocas" ──
  const controle = useMemo(() => montarControleTrocas(pacientes, iw?.historico, trocas, solicitacoes), [pacientes, iw, trocas, solicitacoes]);
  const daUnidadeTodas = useMemo(() => controle.linhas.filter(l => unidade === 'Todas' || l.unidade === unidade), [controle, unidade]);
  const trocasDaUnidade = useMemo(() => daUnidadeTodas.filter(l => l.emAtendimento), [daUnidadeTodas]);
  const qtdConferir = daUnidadeTodas.filter(l => l.conferir).length;
  const qtdAlta = daUnidadeTodas.filter(l => l.vinculo === 'alta').length;
  const semHistUnidade = useMemo(() => controle.semHistorico.filter(l => unidade === 'Todas' || l.unidade === unidade), [controle, unidade]);
  const resumoTrocas = useMemo(() => {
    const r = { vencida: 0, ate7: 0, ate30: 0, emDia: 0, semData: 0 };
    trocasDaUnidade.forEach(l => { r[l.situacao.id]++; });
    return r;
  }, [trocasDaUnidade]);
  const linhasTroca = useMemo(() => {
    if (situacao === 'semHistorico') return [];
    const base = situacao === 'conferir' ? daUnidadeTodas.filter(l => l.conferir)
      : situacao === 'alta' ? daUnidadeTodas.filter(l => l.vinculo === 'alta')
      : trocasDaUnidade.filter(l => !situacao || l.situacao.id === situacao);
    return base.filter(casaBusca).sort((a, b) => (a.dias ?? 99999) - (b.dias ?? 99999));
  }, [trocasDaUnidade, daUnidadeTodas, situacao, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const linhasSemHist = useMemo(() => semHistUnidade.filter(casaBusca).sort((a, b) => a.nome.localeCompare(b.nome)), [semHistUnidade, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const ultimaPlanilha = useMemo(() => trocas.reduce((m, t) => (t.registrado_em > m ? t.registrado_em : m), ''), [trocas]);

  const paginar = arr => arr.slice((pagina - 1) * porPagina, pagina * porPagina);
  const atualizado = iw?.atualizadoEm ? new Date(iw.atualizadoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  const abaBtn = id => ({ border: 0, borderBottom: `3px solid ${aba === id ? P.primary : 'transparent'}`, background: 'none', padding: '10px 4px', marginRight: 18,
    fontSize: '0.92rem', fontWeight: aba === id ? 800 : 600, color: aba === id ? P.text : P.muted, cursor: 'pointer' });

  return (
    <div style={{ padding: embutido ? 0 : '24px 28px', background: embutido ? 'transparent' : P.bg, minHeight: embutido ? 0 : '100%', color: P.text, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          {!embutido && <>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: P.primaryDark }}>Homecare — Assistencial</div>
          <h1 style={{ margin: '4px 0', fontSize: '1.6rem', fontWeight: 800 }}>Controle dos Dispositivos</h1>
          </>}
          <div style={{ fontSize: '0.86rem', color: P.muted }}>
            Dados do IW · exportação de {atualizado} · {daUnidade.length} pacientes em atendimento{unidade !== 'Todas' ? ` na ${unidade}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <label style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: -9999 }}>Buscar paciente ou nº IW</span>
            <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: '#94a3b8' }} />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar paciente ou nº IW"
              style={{ padding: '9px 12px 9px 32px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: '0.86rem', width: 230 }} />
          </label>
          <div style={{ display: 'flex', gap: 4, background: '#e2e8f0', padding: 4, borderRadius: 10 }}>
            {UNIDADES.map(u => (
              <button key={u} onClick={() => setUnidade(u)}
                style={{ border: 0, borderRadius: 8, padding: '7px 13px', fontSize: '0.8rem', fontWeight: unidade === u ? 800 : 600, cursor: 'pointer', background: unidade === u ? P.white : 'transparent', color: P.text }}>
                {u} · {contagemUnidade[u] ?? 0}
              </button>
            ))}
          </div>
          <button onClick={carregar} title="Recarregar" aria-label="Recarregar" style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 10, padding: 9, cursor: 'pointer', display: 'flex' }}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {erro && <div style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 10, padding: '10px 14px', fontSize: '0.86rem' }}>Não foi possível carregar: {erro}</div>}
      {carregando && <div style={{ color: P.muted, fontSize: '0.9rem' }}>Carregando dados do IW e das trocas…</div>}

      {!carregando && !erro && (
        <>
          {!embutido && (
            <div style={{ borderBottom: `1px solid ${P.border}` }}>
              <button style={abaBtn('uso')} onClick={() => setAba('uso')}>Dispositivos em uso</button>
              <button style={abaBtn('trocas')} onClick={() => setAba('trocas')}>Controle de trocas</button>
            </div>
          )}

          {aba === 'uso' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: 12 }}>
                {cards.map(d => {
                  const ativo = disp === d.id;
                  return (
                    <button key={d.id} onClick={() => setDisp(ativo ? null : d.id)}
                      style={{ textAlign: 'left', background: ativo ? P.primarySoft : P.white, border: `1px solid ${ativo ? P.primary : P.border}`, borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: P.muted }}>{d.nome}</span>
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontSize: '1.7rem', fontWeight: 800 }}>{d.sim}</span>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: P.primaryDark }}>{pct(d.sim)}%</span>
                      </span>
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{d.detalhe}</span>
                      <span style={{ height: 6, borderRadius: 6, background: '#e0e7ff', overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: 6, borderRadius: 6, background: P.primary, width: `${Math.max(2, pct(d.sim))}%` }} />
                      </span>
                    </button>
                  );
                })}
              </div>

              <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: `1px solid ${P.border}` }}>
                  <div style={{ fontWeight: 800 }}>
                    Pacientes e dispositivos em uso
                    {disp && <span style={{ fontWeight: 600, color: P.muted }}> · filtrando: {disp === 'semInfo' ? 'sem informação de GTT/TQT/SVD no IW' : DISPOSITIVOS_IW.find(x => x.id === disp)?.nome}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setDisp(disp === 'semInfo' ? null : 'semInfo')}
                      style={{ fontSize: '0.76rem', fontWeight: 700, padding: '6px 12px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${disp === 'semInfo' ? '#f97316' : '#fed7aa'}`, background: '#fff7ed', color: '#9a3412' }}>
                      Sem informação no IW
                    </button>
                    {disp && <button onClick={() => setDisp(null)} style={{ fontSize: '0.76rem', fontWeight: 600, padding: '6px 12px', borderRadius: 999, border: `1px solid ${P.border}`, background: P.white, cursor: 'pointer' }}>Limpar filtro</button>}
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', minWidth: 820 }}>
                    <thead>
                      <tr style={{ background: P.bg, textAlign: 'left', color: P.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        {['Paciente', 'Nº IW', 'Unidade', 'Complexidade', 'Dispositivos', 'Oxigênio', 'Lesão por pressão'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {paginar(linhasUso).map(p => {
                        const usados = DISPOSITIVOS_IW.filter(d => !['o2', 'lpp'].includes(d.id) && p.dispositivos[d.id]?.valor === 'sim');
                        const o2 = p.dispositivos.o2;
                        return (
                          <tr key={p.numIW} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ ...tdStyle, fontWeight: 700 }}>{p.nome}</td>
                            <td style={{ ...tdStyle, color: P.muted }}>{p.numIW}</td>
                            <td style={tdStyle}>{p.unidade}</td>
                            <td style={tdStyle}>{p.complexidade || '—'}</td>
                            <td style={tdStyle}>
                              <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {usados.length === 0 && <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>Nenhum</span>}
                                {usados.map(d => <span key={d.id} style={chip(P.primarySoft, P.primaryDark)}>{SIGLA[d.id]}</span>)}
                              </span>
                            </td>
                            <td style={tdStyle}>{o2?.valor === 'sim' ? (o2.texto ? o2.texto.charAt(0).toUpperCase() + o2.texto.slice(1) : 'Sim') : o2?.valor === 'nao' ? 'Não' : '—'}</td>
                            <td style={tdStyle}>{p.dispositivos.lpp?.valor === 'sim' ? 'Sim' : p.dispositivos.lpp?.valor === 'nao' ? 'Não' : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Paginacao total={linhasUso.length} pagina={pagina} setPagina={setPagina} porPagina={porPagina} setPorPagina={setPorPagina} />
              </div>
            </>
          )}

          {aba === 'trocas' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
                {[
                  ['vencida', 'Vencidas', resumoTrocas.vencida, '#991b1b', '#fee2e2'],
                  ['ate7', 'Vencem em 7 dias', resumoTrocas.ate7, '#9a3412', '#ffedd5'],
                  ['ate30', 'Vencem em 30 dias', resumoTrocas.ate30, '#854d0e', '#fef9c3'],
                  ['emDia', 'Em dia', resumoTrocas.emDia, '#166534', '#dcfce7'],
                  ['semHistorico', 'Sem histórico de troca', semHistUnidade.length, '#1e3a8a', '#dbeafe'],
                  ['conferir', 'Conferir com o IW', qtdConferir, '#475569', '#f1f5f9'],
                  ['alta', 'Alta no IW', qtdAlta, '#475569', '#f1f5f9'],
                ].map(([id, rotulo, valor, cor, fundo]) => {
                  const ativo = situacao === id;
                  return (
                    <button key={id} onClick={() => setSituacao(ativo ? null : id)}
                      style={{ textAlign: 'left', background: ativo ? fundo : P.white, border: `1px solid ${ativo ? cor : P.border}`, borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column', gap: 4, cursor: 'pointer' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: P.muted }}>{rotulo}</span>
                      <span style={{ fontSize: '1.7rem', fontWeight: 800, color: cor }}>{valor}</span>
                    </button>
                  );
                })}
              </div>

              <div style={{ fontSize: '0.8rem', color: P.muted }}>
                Registros de troca: evoluções do IW, lidas do FTP automaticamente de hora em hora · última leitura com novidade em <strong>{ultimaPlanilha ? new Date(ultimaPlanilha).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</strong>.
                {!podeEditar && ' Você pode consultar; para definir onde será a troca é preciso a permissão de editar esta tela.'}
              </div>

              <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.border}`, fontWeight: 800 }}>
                  {situacao === 'semHistorico' ? 'Pacientes que o IW indica usar o dispositivo, sem nenhuma troca registrada' : 'Próximas trocas (mais urgentes primeiro)'}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  {situacao === 'semHistorico' ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', minWidth: 640 }}>
                      <thead><tr style={{ background: P.bg, textAlign: 'left', color: P.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        {['Paciente', 'Nº IW', 'Unidade', 'Dispositivo (IW)'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {paginar(linhasSemHist).map(l => (
                          <tr key={l.chave} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ ...tdStyle, fontWeight: 700 }}>{l.nome}</td>
                            <td style={{ ...tdStyle, color: P.muted }}>{l.numIW}</td>
                            <td style={tdStyle}>{l.unidade}</td>
                            <td style={tdStyle}>{NOME_FAMILIA[l.familia]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', minWidth: 980 }}>
                      <thead><tr style={{ background: P.bg, textAlign: 'left', color: P.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                        {['Paciente', 'Unidade', 'Dispositivo', 'Onde troca', 'Última troca', 'Próxima troca', 'Prazo', 'Observação'].map(h => <th key={h} style={thStyle}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {paginar(linhasTroca).map(l => (
                          <tr key={l.chave} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ ...tdStyle, fontWeight: 700 }}>{l.nome}<div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 500 }}>{l.numIW ? `IW ${l.numIW}` : ''}</div></td>
                            <td style={tdStyle}>{l.unidade}</td>
                            <td style={tdStyle}>{l.dispositivo}</td>
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                {podeEditar && l.id ? (
                                  <label>
                                    <span style={{ position: 'absolute', left: -9999 }}>Onde será a próxima troca de {l.dispositivo} de {l.nome}</span>
                                    <select value={l.localDefinido} disabled={salvando === l.id} onChange={e => definirLocal(l, e.target.value)}
                                      style={{ fontSize: '0.78rem', padding: '4px 6px', borderRadius: 8, border: `1px solid ${P.border}`,
                                        background: l.localDefinido === 'Hospitalar' ? '#dbeafe' : l.localDefinido === 'Domiciliar' ? '#dcfce7' : P.white }}>
                                      <option value="">A definir</option>
                                      <option value="Domiciliar">Domiciliar</option>
                                      <option value="Hospitalar">Hospitalar</option>
                                    </select>
                                  </label>
                                ) : (
                                  <span style={chip(l.localDefinido === 'Hospitalar' ? '#dbeafe' : l.localDefinido === 'Domiciliar' ? '#dcfce7' : '#f1f5f9', l.localDefinido === 'Hospitalar' ? '#1e3a8a' : l.localDefinido === 'Domiciliar' ? '#166534' : '#475569')}>
                                    {l.localDefinido || 'A definir'}
                                  </span>
                                )}
                                {l.solicitacao && <span style={{ fontSize: '0.7rem', color: '#1e3a8a', fontWeight: 700 }}>Solicitação: {l.solicitacao}</span>}
                                {l.localDefinido === 'Hospitalar' && !l.solicitacao && <a href="/gestao-solicitacao-troca" style={{ fontSize: '0.7rem', fontWeight: 700 }}>Abrir solicitação</a>}
                              </div>
                            </td>
                            <td style={tdStyle}>{fmtData(l.ultima)}</td>
                            <td style={tdStyle}>{fmtData(l.proxima)}{l.origemProxima === 'calculada' && l.proxima ? <div style={{ fontSize: '0.7rem', color: P.muted }}>calculada ({l.periodo} dias)</div> : null}</td>
                            <td style={tdStyle}><span style={chip(l.situacao.fundo, l.situacao.cor)}>{l.situacao.rotulo}</span></td>
                            <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#9a3412' }}>{l.alerta || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <Paginacao total={situacao === 'semHistorico' ? linhasSemHist.length : linhasTroca.length} pagina={pagina} setPagina={setPagina} porPagina={porPagina} setPorPagina={setPorPagina} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
